import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Universal Realtime Sync Hook — Consolidated Channels
 * 
 * 3 channels instead of 8 to reduce WebSocket overhead.
 * 
 * Channel 1: sources (transcripts, audio_recordings, source_categories)
 * Channel 2: codebook (codebook_entries, manual_codes, ai_codes, coded_excerpts)
 * Channel 3: project-meta (projects, memos)
 */
export function useRealtimeSync(projectId: string | undefined) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!projectId) return;

    // ── Channel 1: Sources ──
    const sourcesChannel = supabase
      .channel(`sync-sources-${projectId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'transcripts', filter: `project_id=eq.${projectId}` },
        (payload) => {
          queryClient.invalidateQueries({ queryKey: ['transcripts', projectId] });
          queryClient.invalidateQueries({ queryKey: ['saturation', projectId] });
          // Only invalidate excerpts on analysis completion (UPDATE with status change)
          if (payload.eventType === 'UPDATE') {
            queryClient.invalidateQueries({ queryKey: ['all-excerpts', projectId] });
            queryClient.invalidateQueries({ queryKey: ['excerpt-counts', projectId] });
          }
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'audio_recordings', filter: `project_id=eq.${projectId}` },
        () => {
          queryClient.invalidateQueries({ queryKey: ['audio-recordings', projectId] });
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'source_categories', filter: `project_id=eq.${projectId}` },
        () => {
          queryClient.invalidateQueries({ queryKey: ['source-categories', projectId] });
        }
      )
      .subscribe();

    // ── Channel 2: Codebook + Excerpts ──
    const codebookChannel = supabase
      .channel(`sync-codebook-${projectId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'codebook_entries', filter: `project_id=eq.${projectId}` },
        () => {
          queryClient.invalidateQueries({ queryKey: ['codebook', projectId] });
          queryClient.invalidateQueries({ queryKey: ['codebook-all', projectId] });
          queryClient.invalidateQueries({ queryKey: ['codebook-counts', projectId] });
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'manual_codes' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['manual-codes'] });
          queryClient.invalidateQueries({ queryKey: ['all-excerpts', projectId] });
          queryClient.invalidateQueries({ queryKey: ['codebook-counts', projectId] });
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'ai_codes' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['all-excerpts', projectId] });
          queryClient.invalidateQueries({ queryKey: ['excerpt-counts', projectId] });
        }
      )
      .subscribe();

    // ── Channel 3: Project + Memos ──
    const metaChannel = supabase
      .channel(`sync-meta-${projectId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'projects', filter: `id=eq.${projectId}` },
        () => {
          queryClient.invalidateQueries({
            predicate: (q) => {
              const key = q.queryKey;
              return Array.isArray(key) && (key[0] === 'project' || key[0] === 'projects');
            },
          });
          queryClient.invalidateQueries({ queryKey: ['saturation', projectId] });
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'memos', filter: `project_id=eq.${projectId}` },
        () => {
          queryClient.invalidateQueries({ queryKey: ['memos', projectId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(sourcesChannel);
      supabase.removeChannel(codebookChannel);
      supabase.removeChannel(metaChannel);
    };
  }, [projectId, queryClient]);
}

/**
 * Hook specifically for project-level sync (used in contexts/providers)
 */
export function useGlobalProjectSync() {
  const queryClient = useQueryClient();

  useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled || !session) return;

      const projectsChannel = supabase
        .channel('sync-all-projects')
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'projects' },
          () => {
            queryClient.invalidateQueries({ queryKey: ['projects'] });
          }
        )
        .subscribe();

      cleanupRef = () => {
        supabase.removeChannel(projectsChannel);
      };
    });

    let cleanupRef: (() => void) | null = null;

    return () => {
      cancelled = true;
      cleanupRef?.();
    };
  }, [queryClient]);
}
